<template>
    <main class="login-page">
        <q-card flat bordered class="login-card">
            <q-card-section class="q-pb-none">
                <div class="login-title">NBA Decision Engine</div>

                <div v-if="step === 'credentials'" class="login-subtitle">Sign in to continue</div>

                <div v-else-if="step === 'set_password'" class="login-subtitle">Choose a new password</div>

                <div v-else-if="step === 'otp'" class="login-subtitle">
                    <span v-if="otpSecondsLeft > 0">Enter the 6-digit code. It expires in {{ formatSeconds(otpSecondsLeft) }}.</span>
                    <span v-else>This code has expired.</span>
                </div>

                <div v-else class="login-subtitle">Account locked</div>

                <div v-if="step !== 'credentials'" class="login-account">
                    <span>{{ userId }}</span>
                    <button v-if="step !== 'locked'" type="button" class="login-link" :disabled="submitting" @click="startOver">Use a different account</button>
                </div>
            </q-card-section>

            <q-card-section v-if="step === 'credentials'">
                <q-form @submit.prevent="signIn">
                    <q-input
                        ref="userIdInput"
                        v-model.trim="userId"
                        outlined
                        label="Email ID"
                        autocomplete="username"
                        maxlength="100"
                        :disable="submitting"
                        lazy-rules
                        :rules="[(value) => !!value || 'Enter your email ID']"
                    />

                    <q-input
                        v-model="password"
                        outlined
                        class="q-mt-sm"
                        label="Password"
                        autocomplete="current-password"
                        maxlength="200"
                        :type="showPassword ? 'text' : 'password'"
                        :disable="submitting"
                        lazy-rules
                        :rules="[(value) => !!value || 'Enter your password']"
                    >
                        <template #append>
                            <q-icon :name="showPassword ? 'visibility_off' : 'visibility'" class="cursor-pointer" @click="showPassword = !showPassword" />
                        </template>
                    </q-input>

                    <login-messages :error="error" :notice="notice" />

                    <q-btn type="submit" color="primary" unelevated no-caps class="full-width login-button" label="Sign in" :loading="submitting" :disable="submitting" />
                </q-form>
            </q-card-section>

            <q-card-section v-else-if="step === 'set_password'">
                <q-form @submit.prevent="savePassword">
                    <q-input
                        ref="newPasswordInput"
                        v-model="newPassword"
                        outlined
                        label="New password"
                        autocomplete="new-password"
                        maxlength="200"
                        :type="showNewPassword ? 'text' : 'password'"
                        :disable="submitting"
                    >
                        <template #append>
                            <q-icon :name="showNewPassword ? 'visibility_off' : 'visibility'" class="cursor-pointer" @click="showNewPassword = !showNewPassword" />
                        </template>
                    </q-input>

                    <ul class="password-rules">
                        <li v-for="rule in passwordRules" :key="rule.label" :class="{ met: rule.met }">
                            <q-icon :name="rule.met ? 'check_circle' : 'radio_button_unchecked'" size="15px" />
                            <span>{{ rule.label }}</span>
                        </li>
                    </ul>

                    <q-input
                        v-model="confirmPassword"
                        outlined
                        label="Confirm new password"
                        autocomplete="new-password"
                        maxlength="200"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :disable="submitting"
                        :error="confirmPassword.length > 0 && confirmPassword !== newPassword"
                        error-message="Both passwords must match"
                    >
                        <template #append>
                            <q-icon :name="showConfirmPassword ? 'visibility_off' : 'visibility'" class="cursor-pointer" @click="showConfirmPassword = !showConfirmPassword" />
                        </template>
                    </q-input>

                    <login-messages :error="error" :notice="notice" />

                    <q-btn type="submit" color="primary" unelevated no-caps class="full-width login-button" label="Save password and send code" :loading="submitting" :disable="submitting || !newPasswordIsValid" />
                </q-form>
            </q-card-section>

            <q-card-section v-else-if="step === 'otp'">
                <q-form @submit.prevent="verifyCode">
                    <div class="otp-inputs" role="group" aria-label="Six-digit verification code">
                        <input
                            v-for="(digit, index) in otpDigits"
                            :key="index"
                            ref="otpInputs"
                            :value="digit"
                            class="otp-input"
                            type="text"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            pattern="[0-9]*"
                            maxlength="1"
                            :aria-label="`Verification code digit ${index + 1}`"
                            :disabled="submitting || otpSecondsLeft === 0"
                            @input="updateOtpDigit(index, $event)"
                            @keydown.backspace="handleOtpBackspace(index, $event)"
                            @paste="pasteOtp($event)"
                        >
                    </div>

                    <login-messages :error="error" :notice="notice" />

                    <q-btn
                        v-if="otpSecondsLeft > 0"
                        type="submit"
                        color="primary"
                        unelevated
                        no-caps
                        class="full-width login-button"
                        label="Verify and sign in"
                        :loading="submitting"
                        :disable="submitting || otp.length !== 6"
                    />
                    <q-btn
                        v-else
                        type="button"
                        color="primary"
                        unelevated
                        no-caps
                        class="full-width login-button"
                        :label="resendLabel"
                        :loading="submitting"
                        :disable="!canResend"
                        @click="resendCode"
                    />

                    <div v-if="otpSecondsLeft > 0" class="login-footnote">
                        <button type="button" class="login-link" :disabled="!canResend" @click="resendCode">{{ resendLabel }}</button>
                    </div>
                    <div v-else-if="resendsLeft === 0" class="login-footnote">
                        No codes left for this sign-in.
                        <button type="button" class="login-link" @click="startOver">Start again</button>
                    </div>
                </q-form>
            </q-card-section>

            <q-card-section v-else>
                <login-messages :error="error" :notice="notice" />
                <q-btn type="button" color="primary" unelevated no-caps class="full-width login-button" label="Back to sign in" @click="startOver" />
            </q-card-section>
        </q-card>
    </main>
</template>

<script>
import LoginMessages from "../components/LoginMessages.vue";
import { resendOtp, saveSession, submitCredentials, submitNewPassword, verifyOtp } from "../auth.js";
import { forgetSiThoughtTraces } from "../seenSiTraces.js";
import { forgetBcoThoughtTraces } from "../seenBcoTraces.js";

const OTP_LENGTH = 6;

export default {
    name: "LoginView",

    components: { LoginMessages },

    data() {
        return {
            step: "credentials",
            userId: "",
            password: "",
            showPassword: false,
            newPassword: "",
            confirmPassword: "",
            showNewPassword: false,
            showConfirmPassword: false,
            otpDigits: Array(OTP_LENGTH).fill(""),
            loginToken: "",
            otpMeta: null,
            error: "",
            notice: "",
            submitting: false,
            now: Date.now(),
            clock: null,
        };
    },

    computed: {
        otp() {
            return this.otpDigits.join("");
        },

        otpSecondsLeft() {
            return this.secondsUntil(this.otpMeta?.expires_at);
        },

        resendSecondsLeft() {
            return this.secondsUntil(this.otpMeta?.resend_available_at);
        },

        resendsLeft() {
            return this.otpMeta?.resends_left ?? 0;
        },

        canResend() {
            return !this.submitting && this.resendSecondsLeft === 0 && this.resendsLeft > 0;
        },

        resendLabel() {
            if (this.resendSecondsLeft > 0) return `Send a new code in ${this.formatSeconds(this.resendSecondsLeft)}`;
            if (this.resendsLeft === 0) return "No codes left";
            return "Send a new code";
        },

        passwordRules() {
            const value = this.newPassword;
            return [
                { label: "10 to 20 characters", met: value.length >= 10 && value.length <= 20 },
                { label: "An uppercase letter", met: /[A-Z]/.test(value) },
                { label: "A lowercase letter", met: /[a-z]/.test(value) },
                { label: "A number", met: /[0-9]/.test(value) },
                { label: "One of ! @ # $ % ^ & * _", met: /[!@#$%^&*_]/.test(value) },
            ];
        },

        newPasswordIsValid() {
            return this.passwordRules.every((rule) => rule.met) && this.newPassword === this.confirmPassword;
        },
    },

    mounted() {
        this.clock = setInterval(() => {
            this.now = Date.now();
        }, 500);
        this.$refs.userIdInput?.focus();
    },

    beforeUnmount() {
        if (this.clock) clearInterval(this.clock);
    },

    methods: {

        async signIn() {
            if (this.submitting || !this.userId || !this.password) return;
            await this.run(() => submitCredentials(this.userId, this.password));
        },

        async savePassword() {
            if (this.submitting || !this.newPasswordIsValid) return;
            await this.run(() => submitNewPassword(this.loginToken, this.newPassword));
        },

        async resendCode() {
            if (!this.canResend) return;
            await this.run(() => resendOtp(this.loginToken));
        },

        async verifyCode() {
            if (this.submitting || this.otp.length !== OTP_LENGTH || this.otpSecondsLeft === 0) return;
            const data = await this.run(() => verifyOtp(this.loginToken, this.otp));
            if (!data?.error) return;
            this.resetOtpDigits();
            this.$nextTick(() => this.$refs.otpInputs?.[0]?.focus());
        },

        async run(request) {
            this.submitting = true;
            try {
                const data = await request();
                this.applyResponse(data);
                return data;
            } finally {
                this.submitting = false;
            }
        },

        applyResponse(data) {
            if (!data) return;

            this.error = data.error || "";
            this.notice = data.notice || "";
            if (data.login_token) this.loginToken = data.login_token;

            // A different expiry means the server issued a new code, which is the
            // only time the digits already on screen are worth discarding.
            const codeWasReissued = Boolean(data.otp) && data.otp.expires_at !== this.otpMeta?.expires_at;
            if (data.otp) this.otpMeta = data.otp;
            if (codeWasReissued) this.resetOtpDigits();

            if (data.step === "authenticated") {
                forgetSiThoughtTraces();
                forgetBcoThoughtTraces();
                saveSession(data);
                this.$router.push({ name: "home" });
                return;
            }

            // A response without a step reports something that does not move the
            // user, such as a rate limit, so leave them where they are.
            if (!data.step || data.step === this.step) return;
            this.goToStep(data.step);
        },

        goToStep(step) {
            if (step === "credentials" || step === "locked") {
                this.loginToken = "";
                this.password = "";
                this.otpMeta = null;
                this.resetOtpDigits();
            }
            if (step === "credentials" || step === "set_password") {
                this.newPassword = "";
                this.confirmPassword = "";
            }

            this.step = step;
            this.$nextTick(() => {
                if (step === "credentials") this.$refs.userIdInput?.focus();
                if (step === "set_password") this.$refs.newPasswordInput?.focus();
                if (step === "otp") this.$refs.otpInputs?.[0]?.focus();
            });
        },

        startOver() {
            this.error = "";
            this.notice = "";
            this.userId = "";
            this.goToStep("credentials");
        },

        secondsUntil(timestamp) {
            const target = new Date(timestamp).getTime();
            if (!Number.isFinite(target)) return 0;
            return Math.max(0, Math.ceil((target - this.now) / 1000));
        },

        formatSeconds(totalSeconds) {
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `${minutes}:${String(seconds).padStart(2, "0")}`;
        },

        resetOtpDigits() {
            this.otpDigits = Array(OTP_LENGTH).fill("");
        },

        updateOtpDigit(index, event) {
            const digit = event.target.value.replace(/\D/g, "").slice(-1);
            this.otpDigits.splice(index, 1, digit);
            event.target.value = digit;
            if (digit && index < OTP_LENGTH - 1) {
                this.$nextTick(() => this.$refs.otpInputs[index + 1]?.focus());
            }
        },

        handleOtpBackspace(index, event) {
            if (this.otpDigits[index] || index === 0) return;
            event.preventDefault();
            this.otpDigits.splice(index - 1, 1, "");
            this.$nextTick(() => this.$refs.otpInputs[index - 1]?.focus());
        },

        pasteOtp(event) {
            const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
            if (!digits) return;
            event.preventDefault();
            this.otpDigits = digits.padEnd(OTP_LENGTH, " ").split("").map((digit) => digit.trim());
            this.$nextTick(() => this.$refs.otpInputs[Math.min(digits.length, OTP_LENGTH) - 1]?.focus());
        },
    },
};
</script>

<style scoped>
.login-page {
    align-items: center;
    background: linear-gradient(145deg, #edf3fb 0%, #f8fafc 55%, #e8f0fb 100%);
    display: flex;
    font-family: "DM Sans", sans-serif;
    justify-content: center;
    min-height: 100vh;
    padding: 24px;
}

.login-card {
    background: #fff;
    border-color: #e5e8ed;
    border-radius: 18px;
    box-shadow: 0 16px 48px rgba(37, 104, 193, 0.12);
    padding: 20px;
    width: min(100%, 420px);
}

.login-title {
    color: #262626;
    font-size: 24px;
    font-weight: 700;
}

.login-subtitle {
    color: #737373;
    font-size: 15px;
    margin-top: 6px;
}

.login-account {
    align-items: baseline;
    color: #262626;
    display: flex;
    flex-wrap: wrap;
    font-size: 14px;
    font-weight: 600;
    gap: 10px;
    margin-top: 14px;
}

.login-link {
    background: none;
    border: 0;
    color: #2568c1;
    cursor: pointer;
    font: inherit;
    font-weight: 600;
    padding: 0;
    text-decoration: underline;
}

.login-link:disabled {
    color: #9aa5b1;
    cursor: default;
    text-decoration: none;
}

.login-button {
    border-radius: 24px;
    height: 46px;
}

.login-footnote {
    color: #737373;
    font-size: 13px;
    margin-top: 14px;
    text-align: center;
}

.password-rules {
    display: grid;
    gap: 4px;
    list-style: none;
    margin: 10px 0 18px;
    padding: 0;
}

.password-rules li {
    align-items: center;
    color: #8a949f;
    display: flex;
    font-size: 13px;
    gap: 6px;
}

.password-rules li.met {
    color: #1f7a45;
}

.otp-inputs {
    display: flex;
    gap: 8px;
    justify-content: space-between;
}

.otp-input {
    border: 1px solid #c7d0dc;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 22px;
    height: 52px;
    outline: none;
    text-align: center;
    width: 48px;
}

.otp-input:disabled {
    background: #f4f6f9;
    color: #9aa5b1;
}

.otp-input:focus {
    border-color: #2568c1;
    box-shadow: 0 0 0 2px rgba(37, 104, 193, 0.16);
}
</style>
